// app/javascript/controllers/exit_intent_controller.js
import { Controller } from "@hotwired/stimulus"

// Configuration constants
const TIME_BEFORE_MODAL_ARMED = 2500 // ms - time before modal can be triggered
const CLICK_PAUSE_DURATION = 400 // ms - pause after clicks to prevent false triggers
const SCROLL_SAMPLE_SIZE = 5 // number of scroll positions to track for velocity calculation
const SCROLL_UP_VELOCITY_THRESHOLD = -15 // pixels per sample - negative = scrolling up
const SCROLL_TOP_THRESHOLD = 100 // pixels from top to trigger on scroll up
const MOBILE_BREAKPOINT = 768 // pixels - below this is considered mobile

export default class extends Controller {
  static targets = ["modal", "backdrop", "container"]

  static values = {
    context: String
  }

  connect() {
    // Don't initialize if already shown in this session
    if (sessionStorage.getItem(this.storageKey)) return

    this.isReady = false
    this.modalShown = false
    this.scrollPositions = []
    this.lastScrollTime = Date.now()

    // Modal should only be able to open once user has spent X seconds on site
    this.timer = setTimeout(() => {
      this.isReady = true
    }, TIME_BEFORE_MODAL_ARMED)

    // Desktop: Mouse exit detection
    this.boundCheckMouseIntent = this.checkMouseIntent.bind(this)
    document.addEventListener("mouseout", this.boundCheckMouseIntent)

    // Mobile: Scroll up detection
    this.boundCheckScrollIntent = this.checkScrollIntent.bind(this)
    window.addEventListener("scroll", this.boundCheckScrollIntent, { passive: true })

    // Mobile & Desktop: Back button / navigation detection
    this.boundHandleBackButton = this.handleBackButton.bind(this)
    this.setupBackButtonDetection()

    // Click pause to prevent false triggers
    this.clickPause = false
    this.boundHandleClick = this.handleClick.bind(this)
    document.addEventListener("mousedown", this.boundHandleClick)
    document.addEventListener("touchstart", this.boundHandleClick, { passive: true })
  }

  disconnect() {
    document.removeEventListener("mouseout", this.boundCheckMouseIntent)
    window.removeEventListener("scroll", this.boundCheckScrollIntent)
    document.removeEventListener("mousedown", this.boundHandleClick)
    document.removeEventListener("touchstart", this.boundHandleClick)
    window.removeEventListener("popstate", this.boundHandleBackButton)

    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  // ============================================
  // Desktop: Mouse Exit Detection
  // ============================================

  checkMouseIntent(e) {
    if (!this.canShowModal()) return

    // Check if mouse left viewport at the top
    if (!e.relatedTarget && e.clientY <= 0) {
      this.triggerModal('mouse_exit')
    }
  }

  // ============================================
  // Mobile: Scroll Up Detection
  // ============================================

  checkScrollIntent() {
    if (!this.canShowModal()) return
    if (!this.isMobile()) return // Only use scroll detection on mobile

    const currentScrollY = window.scrollY
    const currentTime = Date.now()

    // Add current position to tracking array
    this.scrollPositions.push({
      y: currentScrollY,
      time: currentTime
    })

    // Keep only recent positions
    if (this.scrollPositions.length > SCROLL_SAMPLE_SIZE) {
      this.scrollPositions.shift()
    }

    // Need enough samples to calculate velocity
    if (this.scrollPositions.length < SCROLL_SAMPLE_SIZE) return

    // Calculate scroll velocity (pixels per sample)
    const oldestPosition = this.scrollPositions[0]
    const newestPosition = this.scrollPositions[this.scrollPositions.length - 1]
    const scrollDelta = newestPosition.y - oldestPosition.y
    const velocity = scrollDelta / (this.scrollPositions.length - 1)

    // Trigger if:
    // 1. User is scrolling up quickly (negative velocity)
    // 2. User is near the top of the page
    if (velocity < SCROLL_UP_VELOCITY_THRESHOLD && currentScrollY < SCROLL_TOP_THRESHOLD) {
      this.triggerModal('scroll_up')
    }
  }

  // ============================================
  // Mobile & Desktop: Back Button Detection
  // ============================================

  setupBackButtonDetection() {
    // Push a state to history so we can detect back button
    if (window.history && window.history.pushState) {
      // Only push state if we haven't already
      if (!window.history.state?.exitIntentSet) {
        window.history.pushState({ exitIntentSet: true, context: this.contextValue }, '')
      }

      window.addEventListener("popstate", this.boundHandleBackButton)
    }
  }

  handleBackButton(e) {
    if (!this.canShowModal()) {
      // If we can't show modal, let navigation proceed
      return
    }

    // Prevent the back navigation by pushing state again
    window.history.pushState({ exitIntentSet: true, context: this.contextValue }, '')

    // Show the modal
    this.triggerModal('back_button')
  }

  // ============================================
  // Click Pause (prevents false triggers)
  // ============================================

  handleClick() {
    this.clickPause = true

    setTimeout(() => {
      this.clickPause = false
    }, CLICK_PAUSE_DURATION)
  }

  // ============================================
  // Modal Display Logic
  // ============================================

  canShowModal() {
    return this.isReady && !this.modalShown && !this.clickPause
  }

  triggerModal(trigger = 'unknown') {
    if (this.modalShown) return

    this.modalShown = true
    this.showModal()

    // Mark as shown in session storage
    sessionStorage.setItem(this.storageKey, "true")

    // Remove event listeners since we've shown the modal
    document.removeEventListener("mouseout", this.boundCheckMouseIntent)
    window.removeEventListener("scroll", this.boundCheckScrollIntent)
    window.removeEventListener("popstate", this.boundHandleBackButton)

    // Dispatch custom event for analytics tracking
    this.element.dispatchEvent(new CustomEvent('exit-intent:shown', {
      bubbles: true,
      detail: {
        trigger: trigger,
        context: this.contextValue,
        isMobile: this.isMobile()
      }
    }))
  }

  showModal() {
    // Remove hidden to render the element
    this.modalTarget.classList.remove("hidden")

    // Force a reflow so the browser registers the element as visible
    this.modalTarget.offsetHeight

    // Trigger the animations
    this.backdropTarget.classList.remove("opacity-0")
    this.backdropTarget.classList.add("opacity-100")

    this.containerTarget.classList.remove("opacity-0", "scale-95")
    this.containerTarget.classList.add("opacity-100", "scale-100")

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'
  }

  closeModal() {
    // Reverse the animations
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0")

    this.containerTarget.classList.remove("opacity-100", "scale-100")
    this.containerTarget.classList.add("opacity-0", "scale-95")

    // Re-enable body scroll
    document.body.style.overflow = ''

    // Wait for animation to complete, then hide
    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
    }, 300) // Match the duration-300 in Tailwind
  }

  // ============================================
  // Utility Methods
  // ============================================

  isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT
  }

  get storageKey() {
    return `exit_intent_${this.contextValue || 'global'}`
  }
}
